(() => {
  if (customElements.get('ac-cookie-consent')) return;
  const dismissedKey = 'ac:cookie:ui-dismissed';
  class AcCookieConsent extends HTMLElement {
    #events;
    #styles;
    #checking = true;
    #due = false;
    #saving = false;
    #generation = 0;
    #closing;
    #animation;
    #resize;
    #opener;
    get blocksNewsletter() { return this.#checking || this.dataset.state !== 'closed' || !!document.querySelector('#shopify-pc__banner'); }
    connectedCallback() {
      this.#events?.abort();
      this.#events = new AbortController();
      const { signal } = this.#events;
      this.dataset.state = 'closed';
      this.querySelector('[data-ac-cookie-panel]').inert = true;
      this.addEventListener('click', this.#click, { signal });
      this.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); this.close(); } }, { signal });
      document.addEventListener('click', async event => {
        const trigger = event.target.closest('[data-ac-cookie-open]');
        if (!trigger) return;
        event.preventDefault();
        const header = trigger.closest('ac-reference-header');
        // Reserve priority during the menu's exit so automatic signup waits.
        this.#checking = true; this.#notify();
        try {
          await header?.close?.({ restoreFocus: false });
          const opener = header?.querySelector('[data-ac-navigation-trigger="menu"]') || trigger;
          await this.open({ trigger: opener });
        } finally { this.#checking = false; this.#notify(); }
      }, { signal });
      document.addEventListener('ac:layer:change', this.#tryAuto, { signal });
      document.addEventListener('visibilitychange', this.#tryAuto, { signal });
      document.addEventListener('visitorConsentCollected', () => {
        if (!this.#saving) this.#read().then(async () => {
          this.#due = await window.AcCustomerPrivacy.shouldShow(); this.#notify(); this.#tryAuto();
        }).catch(() => {});
      }, { signal });
      this.#resize = new ResizeObserver(() => {
        const body = this.querySelector('[data-ac-cookie-body]');
        if (this.querySelector('[data-ac-cookie-toggle]').getAttribute('aria-expanded') === 'true' && !this.#animation) body.style.height = `${body.firstElementChild.scrollHeight}px`;
      });
      this.#resize.observe(this.querySelector('[data-ac-cookie-settings]'));
      if (document.readyState === 'complete') this.#init();
      else window.addEventListener('load', this.#init, { once: true, signal });
    }
    disconnectedCallback() {
      this.#generation++;
      this.#events?.abort();
      this.#resize?.disconnect();
      this.#animation?.cancel();
      this.#checking = false;
      this.dataset.state = 'closed';
      this.#notify();
    }
    #notify() { document.dispatchEvent(new CustomEvent('ac:cookie:change')); }
    #init = async () => {
      try {
        this.#due = await window.AcCustomerPrivacy.shouldShow();
        await this.#read();
      } catch { this.#due = false; }
      finally { this.#checking = false; this.#tryAuto(); this.#notify(); }
    };
    #tryAuto = () => {
      if (!this.#due || this.#saving || this.dataset.state !== 'closed' || document.hidden || !this.isConnected) return;
      let dismissed = false;
      try { dismissed = sessionStorage.getItem(dismissedKey) === 'true'; } catch { /* UI storage is optional. */ }
      if (dismissed || document.querySelector('#shopify-pc__banner') || window.AcLayerManager?.isLocked() || document.querySelector('dialog[open]')) return;
      this.open({ automatic: true });
    };
    async #loadStyles() {
      if (this.#styles) return this.#styles;
      this.#styles = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = this.dataset.cssUrl;
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', () => { link.remove(); this.#styles = null; reject(new Error('Cookie CSS unavailable')); }, { once: true });
        document.head.append(link);
      });
      return this.#styles;
    }
    async open({ trigger = null, settings = false, automatic = false } = {}) {
      if (this.dataset.state === 'closing') await this.#closing;
      if (this.dataset.state === 'open') {
        this.#opener = trigger || this.#opener;
        this.#expand(settings);
        if (!automatic) this.querySelector('[data-ac-cookie-title]').focus({ preventScroll: true });
        return true;
      }
      if (document.querySelector('#shopify-pc__banner, #shopify-pc__prefs')) {
        // One privacy UI per region. Keep the platform UI if it owns this visit.
        if (!automatic) window.privacyBanner?.showPreferences();
        this.#due = false; this.#notify(); return false;
      }
      const generation = ++this.#generation;
      try { await this.#loadStyles(); }
      catch { document.querySelector('ac-overlay-system')?.announce(this.dataset.error); return false; }
      if (!this.isConnected || generation !== this.#generation || window.AcLayerManager?.isLocked()) return false;
      this.#due = false;
      this.#opener = trigger;
      this.dataset.state = 'opening';
      const panel = this.querySelector('[data-ac-cookie-panel]');
      panel.hidden = false; panel.inert = false;
      this.#message('');
      this.#notify();
      this.#read().catch(() => this.#message(this.dataset.error));
      this.#expand(settings, true);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (generation !== this.#generation || !this.isConnected) return;
        this.dataset.state = 'open';
        if (!automatic) this.querySelector('[data-ac-cookie-title]').focus({ preventScroll: true });
      }));
      return true;
    }
    close({ remember = true } = {}) {
      if (this.dataset.state === 'closed') return Promise.resolve();
      if (this.dataset.state === 'closing') return this.#closing;
      this.#generation++;
      this.#due = false;
      if (remember) { try { sessionStorage.setItem(dismissedKey, 'true'); } catch { /* Never stores consent. */ } }
      const restore = this.contains(document.activeElement) && this.#opener;
      this.querySelector('[data-ac-cookie-panel]').inert = true;
      this.dataset.state = 'closing';
      this.#closing = new Promise(resolve => setTimeout(() => {
        this.dataset.state = 'closed';
        this.querySelector('[data-ac-cookie-panel]').hidden = true;
        this.#expand(false, true);
        window.AcLottieManager?.destroyWithin(this);
        if (restore?.isConnected) restore.focus({ preventScroll: true });
        this.#notify(); resolve();
      }, this.#reduced() ? 0 : 700));
      return this.#closing;
    }
    #reduced() { return document.body.dataset.acMotion === 'reduced' || matchMedia('(prefers-reduced-motion: reduce)').matches; }
    async #read() {
      const consent = await window.AcCustomerPrivacy.read();
      for (const input of this.querySelectorAll('[data-ac-cookie-choice]')) input.checked = consent[input.name] === 'yes';
    }
    #expand(expanded, immediate = false) {
      const toggle = this.querySelector('[data-ac-cookie-toggle]'), body = this.querySelector('[data-ac-cookie-body]');
      const from = body.getBoundingClientRect().height;
      this.#animation?.cancel();
      this.#animation = null;
      toggle.setAttribute('aria-expanded', String(expanded));
      body.inert = !expanded;
      const to = expanded ? body.firstElementChild.scrollHeight : 0;
      body.style.height = `${to}px`;
      if (immediate || this.#reduced()) return;
      const animation = body.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        duration: expanded ? 450 : 350, easing: expanded ? 'cubic-bezier(.215,.61,.355,1)' : 'cubic-bezier(.455,.03,.515,.955)',
      });
      this.#animation = animation;
      animation.finished.then(() => { if (this.#animation === animation) this.#animation = null; }).catch(() => {});
    }
    #message(text) { const el = this.querySelector('[data-ac-cookie-status]'); el.textContent = text; el.hidden = !text; }
    async #save(choices) {
      if (this.#saving) return;
      this.#saving = true;
      const controls = [...this.querySelectorAll('button, [data-ac-cookie-choice]')];
      controls.forEach(el => { el.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.#message(this.dataset.saving);
      try {
        await window.AcCustomerPrivacy.save(choices);
        if (!this.isConnected) return;
        this.#message(this.dataset.saved);
        await this.close({ remember: false });
      } catch { if (this.isConnected) this.#message(this.dataset.error); }
      finally {
        controls.forEach(el => { el.disabled = false; });
        this.removeAttribute('aria-busy'); this.#saving = false;
      }
    }
    #click = async event => {
      const button = event.target.closest('button');
      if (!button || this.#saving) return;
      if (button.matches('[data-ac-cookie-dismiss]')) this.close();
      else if (button.matches('[data-ac-cookie-toggle]')) this.#expand(button.getAttribute('aria-expanded') !== 'true');
      else if (button.matches('[data-ac-cookie-cancel]')) {
        await this.#read().catch(() => this.#message(this.dataset.error)); this.#expand(false);
      } else if (button.dataset.acCookieSave) {
        const mode = button.dataset.acCookieSave;
        const choices = Object.fromEntries([...this.querySelectorAll('[data-ac-cookie-choice]')].map(input => [input.name, mode === 'all' || (mode === 'custom' && input.checked)]));
        this.#save(choices);
      }
    };
  }
  customElements.define('ac-cookie-consent', AcCookieConsent);
})();
